import type { ReportRowType } from '~/presentation/reports/common/type';

import {
  EOrderDirectionType,
  type OrderByFields,
} from '~/presentation/reports/ui/ReportTable/types';
import { buildReportQuery, getAnalyticModeFromSearchParams } from '~/modules/report-analytic/utils';
import { EReportColumnKey, EReportControlId } from '~/presentation/reports/common/type';
import { useReportDateTime } from '~/presentation/reports/ui/SelectDate/hooks';
import { useReportControlContext } from '~/modules/report-analytic/contexts';
import { getCacheParameterKey } from '~/modules/analytics';
import { useGetVersion } from '../../useGetVersion';
import { useSearchParams } from '@remix-run/react';
import { useMemo } from 'react';

const ORDER_LISTING_COLUMNS = [
  EReportColumnKey.ORDER_ID,
  EReportColumnKey.GEMX_ID,
  EReportColumnKey.ORDER_DATE,
];

interface UseOrderParameterProps {
  overrideFilters: ReportRowType;
}
export const useReportOrderParameter = ({ overrideFilters }: UseOrderParameterProps) => {
  const { filters, timeDimension } = useReportControlContext();
  const { currentDates, compareCurrentDates } = useReportDateTime({
    hasCampaignFilter: filters[EReportControlId.TAB_CAMPAIGN],
  });
  const { versionIds } = useGetVersion({ currentDates });

  const [searchParams] = useSearchParams();
  const mode = getAnalyticModeFromSearchParams(searchParams);

  const parameter = useMemo(() => {
    const orderBy: OrderByFields = {
      [EReportColumnKey.ORDER_DATE]: {
        field: EReportColumnKey.ORDER_DATE,
        direction: EOrderDirectionType.DESC,
      },
    };

    const query = buildReportQuery({
      mode,
      metrics: ORDER_LISTING_COLUMNS,
      sources: ['sales'],
      dimensions: [],
      dateRange: currentDates,
      compareDateRange: compareCurrentDates,
      filters,
      orderBy,
      timeDimension,
      overrideFilters,
      versionIds,
    });
    return { input: query };
  }, [
    mode,
    currentDates,
    compareCurrentDates,
    filters,
    timeDimension,
    overrideFilters,
    versionIds,
  ]);

  const enabled = useMemo(() => {
    const hasFilters = overrideFilters && Object.keys(overrideFilters).length > 0;

    return !!parameter.input && hasFilters;
  }, [parameter, overrideFilters]);

  return {
    parameterKey: getCacheParameterKey(parameter),
    parameter,
    enabled,
  };
};
